import moment from 'moment';

import {
  OdProtosListingsData_Listing_State as ListingState,
  OdProtosListingsData_PARN_State as ParnState,
} from '../../__generated__/athenaGlobalTypes';
import { ParnDetails_odProtosListingsServicesPartneragentreferralnetwork_getPARNDetails_parnDetails as ParnDetails } from '../../graphql/athena/queries/__generated__/ParnDetails';
import { getReadableDate, ParnLead } from '../../helpers/getAllParnLeadData';

type PossibleStates = ParnState | ListingState;
export const preListingAgreementStates: PossibleStates[] = [
  ParnState.LISTING_INITIATED,
  ParnState.FIRST_CONTACT_ATTEMPTED,
  ParnState.FIRST_CONTACT_MADE,
];

export const didNotConvertOrCloseStates: PossibleStates[] = [
  ParnState.DID_NOT_CONVERT_NOT_BUYING_SELLING,
  ParnState.DID_NOT_CONVERT_COMPETITOR,
  ParnState.DID_NOT_CONVERT_NON_RESPONSIVE,
  ParnState.DID_NOT_CONVERT_NOT_INTERESTED,
  ParnState.DID_NOT_CLOSE_NO_ACCEPTABLE_OFFERS,
  ParnState.DID_NOT_CLOSE_PLANS_CHANGED,
  ParnState.DID_NOT_CLOSE_OTHER,
];

export const ParnStateToTimestampKey: { [key in ParnState]?: string } = {
  [ParnState.FIRST_CONTACT_ATTEMPTED]: 'FirstContactAttempted',
  [ParnState.FIRST_CONTACT_MADE]: 'FirstContactMade',
  [ParnState.SELECTED_PARN]: 'SelectedPARN',
};

export const StateToUserStateForParn = {
  [ParnState.LISTING_INITIATED]: 'New lead',
  [ParnState.FIRST_CONTACT_ATTEMPTED]: 'Attempted first contact',
  [ParnState.FIRST_CONTACT_MADE]: 'First contact made',
  [ParnState.SELECTED_PARN]: 'Listing agreement signed',
  [ListingState.LISTING_ACTIVE]: 'Listed',
  [ListingState.IN_CONTRACT]: 'In contract',
  [ListingState.SOLD]: 'Sold',
  [ParnState.UNKNOWN]: 'Did not convert/close',
  [ParnState.DID_NOT_CONVERT_NOT_BUYING_SELLING]: 'Did not convert - Not buying or selling',
  [ParnState.DID_NOT_CONVERT_COMPETITOR]: 'Did not convert - Competitor',
  [ParnState.DID_NOT_CONVERT_NON_RESPONSIVE]: 'Did not convert - Non-responsive',
  [ParnState.DID_NOT_CONVERT_NOT_INTERESTED]: 'Did not convert - Not interested',
  [ParnState.DID_NOT_CLOSE_NO_ACCEPTABLE_OFFERS]: 'Did not close - No acceptable offers',
  [ParnState.DID_NOT_CLOSE_PLANS_CHANGED]: 'Did not close - Plans changed',
  [ParnState.DID_NOT_CLOSE_OTHER]: 'Did not close - Other',
} as { [key in ParnState | ListingState]: string };

const StateOrder = Object.keys(StateToUserStateForParn) as (ParnState | ListingState)[];

export function getStateIndex(state: ParnState | ListingState | undefined): number {
  if (state !== undefined) {
    return StateOrder.indexOf(state);
  }
  return Infinity;
}

function compareStates(ParnDetailsA: ParnDetails, ParnDetailsB: ParnDetails): number {
  const listingStateA = ParnDetailsA.listing?.state;
  const listingStateB = ParnDetailsB.listing?.state;
  const parnStateA = ParnDetailsA.parn?.state;
  const parnStateB = ParnDetailsB.parn?.state;

  let stateIndexA = listingStateA ? getStateIndex(listingStateA) : Infinity;
  let stateIndexB = listingStateB ? getStateIndex(listingStateB) : Infinity;

  if (stateIndexA === -1 || stateIndexA === Infinity) {
    stateIndexA = parnStateA ? getStateIndex(parnStateA) : Infinity;
  }
  if (stateIndexB === -1 || stateIndexB === Infinity) {
    stateIndexB = parnStateB ? getStateIndex(parnStateB) : Infinity;
  }
  return stateIndexA - stateIndexB;
}

export function sortPARNDetailsByState(details: ParnDetails[]): ParnDetails[] {
  return [...details].sort(compareStates);
}

export function dateToDateStringForParn(date: Date | moment.Moment | undefined): string {
  if (date === undefined) {
    return '';
  }
  if (moment.isMoment(date)) {
    return date.toISOString();
  }
  return new Date(date).toISOString();
}

export function dateStringToDateForParn(date: string): Date | null {
  if (date !== '') {
    return new Date(date);
  }
  return null;
}

function getDisplayStates(parnState: ParnState, listingState: ListingState): string {
  if (StateToUserStateForParn[listingState]) {
    return StateToUserStateForParn[listingState];
  } else if (StateToUserStateForParn[parnState]) {
    return StateToUserStateForParn[parnState];
  }
  return 'Unknown';
}

export function getDisplayStateFromParnLead(parnLead: ParnLead): string {
  const parnState =
    parnLead.parnData.parnState === undefined ? ParnState.UNKNOWN : parnLead.parnData.parnState;
  const listingState =
    parnLead.parnData.listingState === undefined
      ? ListingState.UNKNOWN
      : parnLead.parnData.listingState;
  return getDisplayStates(parnState, listingState);
}

export function getStateForParn(
  parnState?: ParnState,
  listingState?: ListingState,
): ParnState | ListingState {
  if (listingState && listingState in StateToUserStateForParn) {
    return listingState;
  } else if (parnState && parnState in StateToUserStateForParn) {
    return parnState;
  }
  return ParnState.UNKNOWN;
}

function isListingState(status: ParnState | ListingState): status is ListingState {
  return Object.values(ListingState).includes(status as ListingState);
}

function isParnState(status: ParnState | ListingState): status is ParnState {
  return Object.values(ParnState).includes(status as ParnState);
}

export function getStatusDateString(
  status: ParnState | ListingState,
  parnTimestampsMap: Map<string, string>,
  listingsTimestampsMap: Map<ListingState, string>,
): string {
  if (isListingState(status)) {
    return listingsTimestampsMap.get(status) || '';
  } else if (isParnState(status)) {
    const statusString = ParnStateToTimestampKey[status];
    if (statusString) {
      return getReadableDate(parnTimestampsMap.get(statusString)) || '';
    }
  }
  return '';
}

export function getStatusTimestampForParn(
  status: ParnState | ListingState,
  parnTimestampsMap: Map<string, string>,
  listingsTimestampsMap: Map<ListingState, string>,
): Date | null {
  const dateString = getStatusDateString(status, parnTimestampsMap, listingsTimestampsMap);
  return dateStringToDateForParn(dateString);
}

export function findStateFromTimestampKey(stateKey: string | ParnState | ListingState) {
  const foundKey = Object.entries(ParnStateToTimestampKey).find(
    ([__, value]) => value === stateKey,
  );
  return foundKey ? foundKey[0] : stateKey;
}

export function priceToNumber(value: string | number | undefined) {
  if (typeof value === 'number') {
    return value;
  } else if (typeof value === 'string') {
    const numberValue = Number(value.replace(/[^0-9.-]+/g, ''));
    return isNaN(numberValue) ? undefined : numberValue;
  }
  return undefined;
}
